import React, { ReactElement, useCallback } from 'react'
import {
  AccordionIcon,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  Grid,
  GridItem,
  GridItemProps,
  GridProps,
  BoxProps,
  AccordionItemProps,
  forwardRef,
  ChakraComponent,
} from '@chakra-ui/react'
import { useColor } from '../../theme'
import { SectionListItemField } from './SectionListItemField'

interface ISectionListItemFieldStyleProps {
  style: {
    color: BoxProps['color']
    fontSize: BoxProps['fontSize']
  }
}

interface IItemProps extends AccordionItemProps {
  'data-testid'?: string
  alignItemsToValue?: boolean
  description?: ReactElement
  field?: string
  isEditable?: boolean
  isItemDisabled?: boolean
  isSingleton?: boolean
  value?: string
  onItemButtonClick?: (isExpanded: boolean) => void
  renderSectionListItemIcon?: (isExpanded: boolean) => ReactElement
  renderSectionListItemField?: (props: ISectionListItemFieldStyleProps,) => ReactElement
}

const gridItemProps: GridItemProps = {
  alignItems: 'center',
  display: 'flex',
}

export const SHARED_TEMPLATE_COLUMNS: GridProps['templateColumns'] = '1fr 4fr 1fr'

/**
 * Item for the SectionList component.
 *
 * The component wanted to trigger the expand and collapse can be sent via renderSectionListItemIcon props,
 * otherwise it applied the default which is a chevron-icon (arrow up and down).
 *
 * Should be combined with the `SectionList` component.
 */
export const SectionListItem = forwardRef<IItemProps, ChakraComponent<'div'>>(
  (
    {
      value,
      field,
      description,
      children,
      isEditable = true,
      isItemDisabled = false,
      isSingleton = false,
      alignItemsToValue = true,
      onItemButtonClick,
      renderSectionListItemIcon = () => <AccordionIcon />,
      renderSectionListItemField = ({ style }) => (
        <SectionListItemField {...style} text={field} />
      ),
      ...otherProps
    },
    ref?,
  ) => {
    const valueColor = useColor({ nuance: 6, palette: 'text' })
    const fieldColor = useColor({ nuance: 3, palette: 'text' })

    const renderIcon = useCallback(
      (isExpanded: boolean) =>
        isEditable ? renderSectionListItemIcon(isExpanded) : null,
      [renderSectionListItemIcon, isEditable],
    )

    return (
      <AccordionItem
        data-testid="section-list-item-container"
        isDisabled={isItemDisabled}
        {...(isSingleton
          ? {
            marginTop: '-4.5rem',
          }
          : {})}
        {...otherProps}>
        {({ isExpanded }) => (
          <Grid
            columnGap={4}
            templateColumns={SHARED_TEMPLATE_COLUMNS}
            templateRows="repeat(1fr, 2)"
            width="full">
            <GridItem colSpan={3} width="full">
              <AccordionButton
                _hover={{
                  cursor: isEditable ? 'pointer' : 'auto',
                }}
                data-testid="section-list-item-button"
                onClick={() => onItemButtonClick?.(isExpanded)}
                ref={ref}>
                {/* Whenever we get subgrid we can ditch the explicit grid definition in favor of `grid-template-columns: subgrid` */}
                <Grid
                  alignContent="center"
                  columnGap={4}
                  templateColumns={SHARED_TEMPLATE_COLUMNS}
                  templateRows="2rem 1fr"
                  width="full">
                  <GridItem {...gridItemProps}>
                    {renderSectionListItemField({
                      style: { fontSize: 'sm', color: fieldColor },
                    })}
                  </GridItem>
                  <GridItem {...gridItemProps}>
                    {value && (
                      <SectionListItemField
                        fontSize="md"
                        fontWeight="medium"
                        text={value}
                        textColor={valueColor}
                      />
                    )}
                  </GridItem>
                  <GridItem {...gridItemProps} paddingRight={6}>
                    {renderIcon(isExpanded)}
                  </GridItem>
                  {description && (
                    <GridItem
                      {...gridItemProps}
                      colSpan={1}
                      colStart={2}
                      justifyContent="flex-start"
                      marginTop={2}>
                      {description}
                    </GridItem>
                  )}
                </Grid>
              </AccordionButton>
            </GridItem>
            <GridItem
              colSpan={alignItemsToValue ? 2 : 3}
              colStart={alignItemsToValue ? 2 : 1}>
              {isEditable && (
                <AccordionPanel
                  data-testid="section-list-item-panel"
                  display="flex"
                  pb={4}
                  pl={0}>
                  {typeof children === 'function' ? children({ isExpanded, isDisabled: isItemDisabled }) : children}
                </AccordionPanel>
              )}
            </GridItem>
          </Grid>
        )}
      </AccordionItem>
    )
  },
)
